import React, { useState } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import Rut from "rutjs";

// Reemplaza tildes/ñ por versión ASCII
const normalizeNoAccents = (value) => {
  if (typeof value !== "string") return "";
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/Ñ/g, "N");
};

// Limpia saltos/tabs y caracteres peligrosos básicos (< > `)
const sanitizeBasic = (value) => {
  if (typeof value !== "string") return "";
  return value
    .replace(/[\r\n\t]/g, " ") // quita saltos y tabs
    .replace(/[<>`]/g, "")     // quita < > `
    .trimStart();
};

// SOLO letras y números (y espacios) para razonSocial/direccion/giro
// Si NO quieres espacios, cambia el regex a: /[^A-Za-z0-9]/g
const ALNUM_SPACE_REGEX = /[^A-Za-z0-9 ]/g;

const sanitizeAlnum = (value) => {
  if (typeof value !== "string") return "";

  // 1) normaliza tildes/ñ
  const normalized = normalizeNoAccents(value);

  // 2) sanitiza saltos/tabs y < > `
  const cleaned = sanitizeBasic(normalized);

  // 3) deja solo letras/números/espacios
  // 4) compacta espacios múltiples
  return cleaned.replace(ALNUM_SPACE_REGEX, "").replace(/\s+/g, " ");
};

const ModalReceptorForm = ({
  show,
  onHide,
  titulo,
  formData,
  setFormData,
  onConfirm,
  opcionesActividad,
  optionsRegion
}) => {
  const [rutReceptorError, setRutReceptorError] = useState("");
  const [telefonoError, setTelefonoError] = useState("");
  const [emailError, setEmailError] = useState("");

  // Handler normal
  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handler para razonSocial/direccion/giro: solo letras/números/espacios (sin tildes ni comillas)
  const handleInputChangeAlnum = (e, field) => {
    const cleaned = sanitizeAlnum(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [field]: cleaned,
    }));
  };

  const handleSelectChange = (selected) => {
    setFormData({
      ...formData,
      acteco: selected ? selected.value : null,
    });
  };

  const handleRutReceptorChange = (e) => {
    const input = e.target.value;
    const limpio = input.replace(/[^0-9kK]/g, "");

    if (limpio === "") {
      setFormData((prev) => ({ ...prev, rut: "" }));
      setRutReceptorError("");
      return;
    }

    try {
      const rutObj = new Rut(limpio);
      const rutFormateado = rutObj.getNiceRut();
      const esValido = rutObj.validate();

      setFormData((prev) => ({ ...prev, rut: rutFormateado }));
      setRutReceptorError(esValido ? "" : "RUT inválido");
    } catch {
      setFormData((prev) => ({ ...prev, rut: input }));
      setRutReceptorError("RUT inválido");
    }
  };

  const validarEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData((prev) => ({ ...prev, email }));
    setEmailError(email && !validarEmail(email) ? "Formato de correo inválido" : "");
  };

  const formatearTelefonoChileno = (input) => {
    let numeros = input.replace(/\D/g, "");
    if (numeros.startsWith("56")) numeros = numeros.slice(2);
    numeros = numeros.slice(0, 9);
    if (!numeros) return "";

    return numeros.startsWith("9")
      ? `+56 9 ${numeros.slice(1, 5)} ${numeros.slice(5)}`
      : `+56 ${numeros.slice(0, 2)} ${numeros.slice(2)}`;
  };

  const handleTelefonoChange = (e) => {
    const telefonoFormateado = formatearTelefonoChileno(e.target.value);
    const soloNumeros = telefonoFormateado.replace(/\D/g, "").replace(/^56/, "");

    if (!soloNumeros) {
      setFormData((prev) => ({ ...prev, telefono: "" }));
      setTelefonoError("");
      return;
    }

    const valido = soloNumeros.length === 9 && /^[2-9]\d{8}$/.test(soloNumeros);
    setFormData((prev) => ({ ...prev, telefono: telefonoFormateado }));
    setTelefonoError(valido ? "" : "Número inválido. Ej: +56 9 1234 5678");
  };
   const findGroupedOption = (groupedOptions, value) => {
    if (!value) return null;
    for (const group of groupedOptions || []) {
      const found = (group.options || []).find((op) => op.value === value);
      if (found) return found;
    }
    return null;
  };

  const handleComunaSelectChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      comuna: selected ? selected.value : "",
    }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          {/* RUT */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>RUT</Form.Label>
              <Form.Control
                value={formData.rut}
                onChange={handleRutReceptorChange}
                isInvalid={!!rutReceptorError}
                disabled={titulo.toLowerCase().includes("editar")}
              />
              <Form.Control.Feedback type="invalid">
                {rutReceptorError}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Razón Social*/}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Razón Social</Form.Label>
              <Form.Control
                value={formData.razonSocial || ""}
                onChange={(e) => handleInputChangeAlnum(e, "razonSocial")}
                maxLength={100}
              />
            </Form.Group>
          </Col>

          {/* Dirección */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                value={formData.direccion || ""}
                onChange={(e) => handleInputChangeAlnum(e, "direccion")}
                maxLength={70}
              />
            </Form.Group>
          </Col>

           {/* Comuna */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Comuna</Form.Label>

              <Select
                options={optionsRegion}
                value={findGroupedOption(optionsRegion, formData.comuna) || null}
                onChange={handleComunaSelectChange}
                isClearable
                placeholder="Selecciona comuna"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Form.Group>
          </Col>

          {/* Ciudad */}
          <Col md={4} className="mb-3">
            <Form.Group>
              <Form.Label>Ciudad</Form.Label>
              <Form.Control
                value={formData.ciudad || ""}
                onChange={(e) => handleInputChange(e, "ciudad")}
                maxLength={20}
              />
            </Form.Group>
          </Col>

          {/* Giro */}
          <Col md={8} className="mb-3">
            <Form.Group>
              <Form.Label>Giro</Form.Label>
              <Form.Control
                value={formData.giro || ""}
                onChange={(e) => handleInputChangeAlnum(e, "giro")}
                maxLength={80}
              />
            </Form.Group>
          </Col>

          {/* Acteco */}
          <Col md={8} className="mb-3">
            <Form.Group>
              <Form.Label>Actividad Económica</Form.Label>
              <Select
                options={opcionesActividad}
                value={opcionesActividad.find((op) => op.value === formData.acteco) || null}
                onChange={handleSelectChange}
                isClearable
              />
            </Form.Group>
          </Col>

          {/* Teléfono */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                value={formData.telefono}
                onChange={handleTelefonoChange}
                isInvalid={!!telefonoError}
              />
              <Form.Control.Feedback type="invalid">
                {telefonoError}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Email */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={formData.email}
                onChange={handleEmailChange}
                isInvalid={!!emailError}
              />
              <Form.Control.Feedback type="invalid">
                {emailError}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onConfirm}>
          {titulo.includes("Editar") ? "Guardar Cambios" : "Agregar Receptor"}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalReceptorForm;
